/**
 * @file    useFieldsManager.ts
 * @scope   VIEW
 * @summary Orchestration hook behind the Add/Remove Fields screen: loads available fields/
 *          calculations, adds/removes/reorders fields, applies per-calc-type defaults, per-field
 *          filters, expansion/validation UI state, and syncs back to the EnterpriseContext.
 * @consumes EnterpriseContext, FieldsManager models, calc-type default builders, validation utils
 * @produces Everything FieldsManager.tsx renders (field list, handlers, states, counts)
 * @sideEffects Fetches chart-type data; writes the field selection back through the context.
 * @notes Calc types: 1=Mean, 2=Percentage, 3=NPS, 4=TotalCount/Frequency, 6=Median. Per-type defaults live
 *        in the `CALC_TYPE_DEFAULTS` strategy map below — extend it to add a new type without
 *        touching the change handlers. Keep JSX in FieldsManager.tsx declarative; logic lives here.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { arrayMove } from 'helpers/sorting';
import {
  SelectedFieldModel,
  SelectedUserFields,
  FieldsManagerViewOptionsModel,
  FieldModel,
  FieldFilter,
} from 'models/Enterprise/FieldsManagerModel';
import { buildDefaultPercentageSettings } from '../../utils/views/partitionsManager/calculationsManager/percentageSettingsUtils';
import { buildDefaultNpsSettings } from '../../utils/views/partitionsManager/calculationsManager/npsSettingsUtils';
import {
  buildDefaultNumericSettings,
  isNumericField,
} from '../../utils/views/partitionsManager/calculationsManager/numericSettingsUtils';
import { useFieldStates } from 'hooks/Enterprise/useFieldState';
import { getDataSourceFieldCount } from '../../utils/validation/partitionValidation';
import {
  isPercentageSettingsIncomplete,
  isFieldItemInvalid,
} from '../../utils/validation/fieldValidation';
import { areFiltersInvalid, hasIncompleteFilters } from '../../utils/validation/filterValidation';
import {
  buildEndpointWithPartition,
  buildFieldsManagerViewOptions,
  mapCalculationsToSelectOptions,
  mapFieldsToSelectOptions,
  recoverEditModeFields,
} from '../../utils/mapping/fieldMapping';
import { useAxios } from 'hooks/useAxios';
import { useStore } from 'store/useGlobalStore';
import { PartitionModel } from 'models/Enterprise/DataSourceModel';
import { getChartTypeData } from 'services/Torogoz/TogorozApi';
import { ensureProjectFrequencyField } from '../../utils/payload/projectFrequency';

/**
 * Strategy map: calc-type default settings builders.
 * Add new calculation types here without touching handler logic.
 */
const CALC_TYPE_DEFAULTS: Record<
  string,
  {
    buildDefaults: (field: FieldModel) => Partial<SelectedFieldModel>;
    requiresRatingField?: boolean;
    autoTouchWhenIncomplete?: boolean;
  }
> = {
  '1': {
    //Mean
    buildDefaults: () => ({
      isL1Category: false,
    }),
  },
  '2': {
    //Percentage
    buildDefaults: (field) =>
      isNumericField(field)
        ? {
            numericSettings: buildDefaultNumericSettings(),
            percentageSettings: undefined,
            isL1Category: false,
            normalize: false,
          }
        : {
            percentageSettings: buildDefaultPercentageSettings(field),
            numericSettings: undefined,
            isL1Category: false,
            // Auto-enable normalize for SimpleBoolean fields (band-aid for base size functionality)
            normalize: field?.typeName === 'SimpleBoolean' ? true : false,
          },
    autoTouchWhenIncomplete: true,
  },
  '3': {
    //NPS
    buildDefaults: (field) => ({
      npsSettings: buildDefaultNpsSettings(field),
      isL1Category: false,
    }),
    requiresRatingField: true,
  },
  '4': {
    //Frequency
    buildDefaults: (field) =>
      isNumericField(field)
        ? {
            numericSettings: buildDefaultNumericSettings(),
            percentageSettings: undefined,
            isL1Category: false,
            normalize: false,
          }
        : {
            percentageSettings: { path: field?.path ?? 'id', selectedValues: [] },
            numericSettings: undefined,
            isL1Category: false,
            // Auto-enable normalize for SimpleBoolean fields (band-aid for base size functionality)
            normalize: field?.typeName === 'SimpleBoolean' ? true : false,
          },
  },
  '6': {
    //Median
    buildDefaults: () => ({
      isL1Category: false,
    }),
  },
};

const hasScale010ForNps = (field: FieldModel): boolean => {
  if (!field?.responses || !Array.isArray(field.responses)) return false;

  const parseScaleBoundary = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value !== 'string') return null;
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  };

  const minScale =
    parseScaleBoundary(field.NumericRangeMin) ??
    parseScaleBoundary(field.MinRating) ??
    parseScaleBoundary(field.ScaleMin);
  const maxScale =
    parseScaleBoundary(field.NumericRangeMax) ??
    parseScaleBoundary(field.MaxRating) ??
    parseScaleBoundary(field.ScaleMax);

  if (minScale !== null && maxScale !== null) {
    return minScale === 0 && maxScale === 10;
  }

  const parseRating = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value !== 'string') return null;

    const trimmed = value.trim();
    if (!trimmed) return null;

    const match = trimmed.match(/^(\d{1,2})(?:\s*[-–—:]?\s*.*)?$/);
    if (!match) return null;
    const ratingToken = match[1];
    if (!ratingToken) return null;

    const parsed = Number(ratingToken);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const isZeroToTenScale = (values: number[]): boolean => {
    if (values.length === 0) return false;

    const inRange = values.every(
      (rating) => Number.isInteger(rating) && rating >= 0 && rating <= 10
    );
    const hasZero = values.some((rating) => rating === 0);
    const hasTen = values.some((rating) => rating === 10);

    return inRange && hasZero && hasTen;
  };

  const responseValues = field.responses.reduce(
    (acc: { choiceRatings: number[]; numericRatings: number[] }, r: any) => {
      const choiceRating = parseRating(r?.choice);
      if (choiceRating !== null) {
        acc.choiceRatings.push(choiceRating);
      }

      const numericRating = parseRating(r?.rating);
      if (numericRating !== null) {
        acc.numericRatings.push(numericRating);
      }

      return acc;
    },
    { choiceRatings: [], numericRatings: [] }
  );

  if (isZeroToTenScale(responseValues.choiceRatings)) return true;

  return isZeroToTenScale(responseValues.numericRatings);
};

interface UseFieldsManagerParams {
  selectedUserFields: SelectedUserFields;
  fieldsManagerViewOptionsProp?: FieldsManagerViewOptionsModel;
  onSelectedUserFieldsChange?: (fields: SelectedUserFields) => void;
  onViewOptionsLoaded?: (options: FieldsManagerViewOptionsModel) => void;
  endpoint: string;
  selectedPartition: PartitionModel;
  expandedIndex?: number | null;
}

/** Clears 'New Field' placeholder from a field's displayName when it loses the top position. */
const clearNewFieldDisplayName = (fields: SelectedFieldModel[]): SelectedFieldModel[] =>
  fields.map((f) => ({
    ...f,
    displayName: f.displayName === 'New Field' ? '' : f.displayName,
  }));

export const useFieldsManager = ({
  selectedUserFields,
  fieldsManagerViewOptionsProp,
  onSelectedUserFieldsChange,
  onViewOptionsLoaded,
  endpoint,
  selectedPartition,
  expandedIndex = null,
}: UseFieldsManagerParams) => {
  // --- View options state ---
  const [fieldsManagerViewOptions, setFieldsManagerViewOptions] = useState<
    FieldsManagerViewOptionsModel | undefined
  >(fieldsManagerViewOptionsProp);
  const { response: responseData, axiosFetch: axiosFetchData, loading: loadingData } = useAxios();
  const isLoadingOptions = !fieldsManagerViewOptionsProp && !fieldsManagerViewOptions;
  const isLoading = isLoadingOptions || loadingData;
  const { isConsumer, useDiyMetricLevelFilters } = useStore((state) => ({
    isConsumer: state.isConsumer,
    useDiyMetricLevelFilters: state.configDashboard.useDiyMetricLevelFilters,
  }));

  // --- Mapped select options ---
  const availableFields = useMemo(() => {
    if (!fieldsManagerViewOptions?.FieldsOptions) return [];
    return mapFieldsToSelectOptions(
      fieldsManagerViewOptions.FieldsOptions.filter(
        (f) => f.responses.length > 0 || isNumericField(f)
      )
    );
  }, [fieldsManagerViewOptions?.FieldsOptions]);

  const calculationTypes = useMemo(() => {
    if (!fieldsManagerViewOptions?.CalculationTypeOptions) return [];
    return mapCalculationsToSelectOptions(fieldsManagerViewOptions.CalculationTypeOptions);
  }, [fieldsManagerViewOptions?.CalculationTypeOptions]);

  /**\n   * Available filter dimensions (raw models, not mapped to select options).\n   * FiltersTab needs the full FilterOptionModel to access nested values.\n   * Falls back to mock data when the API hasn't provided filters yet.\n   */
  const availableFilterFields = useMemo(() => {
    if (!fieldsManagerViewOptions?.FiltersOptions?.length) return [];
    return fieldsManagerViewOptions.FiltersOptions;
  }, [fieldsManagerViewOptions?.FiltersOptions]);

  const getCalculationTypesForField = useCallback(
    (field: FieldModel): { value: string; label: string }[] => {
      const isRatingQuestion = field?.typeName === 'RatingQuestion';
      const isSimpleBoolean = field?.typeName === 'SimpleBoolean';
      const isNumeric = isNumericField(field);

      return calculationTypes.filter((ct) => {
        const type = String(ct.value);

        // Numeric: Mean (1), Percentage (2), Frequency (4), Median (6)
        if (isNumeric) return ['1', '2', '4', '6'].includes(type);
        if (type === '6') return false;

        // SimpleBoolean: only Frequency (4) or Percentage (2)
        if (isSimpleBoolean && type !== '2' && type !== '4') return false;

        // RatingQuestion: exclude Mean (1) and NPS (3) unless it has 0-10 scale
        if (!isRatingQuestion && (type === '1' || type === '3')) return false;
        if (isRatingQuestion && type === '3') return hasScale010ForNps(field);

        return true;
      });
    },
    [calculationTypes]
  );

  // --- UI state ---
  const [expandedFieldIndices, setExpandedFieldIndices] = useState<Set<number>>(() => {
    if (expandedIndex != null) return new Set([expandedIndex]);
    return selectedUserFields.fields.length <= 1 ? new Set([0]) : new Set();
  });
  const [highlightedFieldIndex, setHighlightedFieldIndex] = useState<number | null>(null);
  const [touchedFieldIndices, setTouchedFieldIndices] = useState<Set<number>>(new Set());
  const [focusedFieldId, setFocusedFieldId] = useState<string | null>(null);

  // --- Refs for scroll (replaces document.getElementById) ---
  const fieldItemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const setFieldItemRef = useCallback((index: number, el: HTMLDivElement | null) => {
    if (el) {
      fieldItemRefs.current.set(index, el);
    } else {
      fieldItemRefs.current.delete(index);
    }
  }, []);

  // --- Field states (existing hook) ---
  const fieldStates = useFieldStates(selectedUserFields.fields, useDiyMetricLevelFilters);

  // --- Data fetching ---
  useEffect(() => {
    if (fieldsManagerViewOptionsProp) return;
    if (endpoint) {
      const url = buildEndpointWithPartition(endpoint, selectedPartition.value);
      getChartTypeData(url, axiosFetchData, isConsumer);
    }
  }, []);

  // --- Filters data fetching (boilerplate — not yet wired) ---
  // TODO: Uncomment and configure once the filters API endpoint is defined.
  // const {
  //   response: filtersResponse,
  //   axiosFetch: axiosFetchFilters,
  //   loading: loadingFilters,
  // } = useAxios();
  //
  // useEffect(() => {
  //   if (!endpoint || !selectedPartition?.value) return;
  //   const filtersUrl = buildEndpointWithPartition(
  //     endpoint,     // TODO: replace with actual filters endpoint
  //     selectedPartition.value
  //   );
  //   getAvailableFilters(filtersUrl, axiosFetchFilters, isConsumer);
  // }, []);
  //
  // useEffect(() => {
  //   if (filtersResponse) {
  //     // TODO: map filtersResponse to FilterOptionModel[]
  //     // const mapped = mapFiltersApiResponse(filtersResponse);
  //     // setFieldsManagerViewOptions((prev) =>
  //     //   prev ? { ...prev, FiltersOptions: mapped } : prev
  //     // );
  //   }
  // }, [filtersResponse]);

  useEffect(() => {
    if (responseData) {
      const options = buildFieldsManagerViewOptions(responseData?.config, selectedPartition);
      setFieldsManagerViewOptions(options);
      onViewOptionsLoaded?.(options);
    }
  }, [responseData]);

  // --- Edit-mode recovery ---
  useEffect(() => {
    if (!fieldsManagerViewOptions || !selectedUserFields.fromEditMode) return;
    const recovered = recoverEditModeFields(selectedUserFields, fieldsManagerViewOptions);
    onSelectedUserFieldsChange?.(recovered);
  }, [fieldsManagerViewOptions]);

  // --- Ensure the pinned "Project Frequency" field is always present at the top ---
  useEffect(() => {
    // Wait until edit-mode recovery has completed so we don't fight its update.
    if (selectedUserFields.fromEditMode) return;

    const partitionLabel =
      fieldsManagerViewOptions?.parentPartition?.label ??
      fieldsManagerViewOptionsProp?.parentPartition?.label ??
      selectedPartition?.label ??
      '';

    const ensuredFields = ensureProjectFrequencyField(selectedUserFields.fields, partitionLabel);
    const hasChanges =
      ensuredFields.length !== selectedUserFields.fields.length ||
      ensuredFields.some((field, index) => field !== selectedUserFields.fields[index]);

    if (!hasChanges) return;

    onSelectedUserFieldsChange?.({
      ...selectedUserFields,
      fields: ensuredFields,
    });
  }, [
    selectedUserFields,
    fieldsManagerViewOptions?.parentPartition?.label,
    fieldsManagerViewOptionsProp?.parentPartition?.label,
    selectedPartition?.label,
    onSelectedUserFieldsChange,
  ]);

  // --- Scroll effects (using refs instead of DOM queries) ---
  useEffect(() => {
    if (highlightedFieldIndex !== null) {
      requestAnimationFrame(() => {
        fieldItemRefs.current
          .get(highlightedFieldIndex)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      const timer = setTimeout(() => setHighlightedFieldIndex(null), 500);
      return () => clearTimeout(timer);
    }
  }, [highlightedFieldIndex]);

  useEffect(() => {
    if (expandedIndex != null) {
      setTouchedFieldIndices((prev) => new Set([...Array.from(prev), expandedIndex]));
      requestAnimationFrame(() => {
        fieldItemRefs.current
          .get(expandedIndex)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }, [expandedIndex]);

  // --- Handlers ---

  const handleAddField = useCallback(() => {
    const fieldNumber = selectedUserFields.fields.length + 1;
    const newField: SelectedFieldModel = {
      fieldNumber,
      field: { fieldGuid: '', fieldName: '' },
      displayName: 'New Field',
      calculationType: null,
      precision: '0.00',
      normalize: false,
      statTesting: false,
      isL1Category: false,
    };
    const newIndex = selectedUserFields.fields.length;
    const updated = {
      ...selectedUserFields,
      fields: [...clearNewFieldDisplayName(selectedUserFields.fields), newField],
    };
    setTouchedFieldIndices(
      (prev) => new Set([...Array.from(prev), ...selectedUserFields.fields.map((_, i) => i)])
    );
    setExpandedFieldIndices(new Set([newIndex]));
    setHighlightedFieldIndex(newIndex);
    onSelectedUserFieldsChange?.(updated);
  }, [selectedUserFields, onSelectedUserFieldsChange]);

  const handleRemoveField = useCallback(
    (index: number) => {
      // The pinned Project Frequency field cannot be removed.
      if (selectedUserFields.fields[index]?.isProjectFrequency) return;
      const updated = {
        ...selectedUserFields,
        fields: selectedUserFields.fields.filter((_, i) => i !== index),
      };
      onSelectedUserFieldsChange?.(updated);
    },
    [selectedUserFields, onSelectedUserFieldsChange]
  );

  const handleFieldSelect = useCallback(
    (index: number, selectedOption: any) => {
      const updated = { ...selectedUserFields, fields: [...selectedUserFields.fields] };
      if (selectedOption) {
        const fieldData = fieldsManagerViewOptions?.FieldsOptions?.find(
          (f) => f.fieldGuid === selectedOption.value
        );
        updated.fields[index].field = fieldData || {
          fieldGuid: selectedOption.value,
          fieldName: selectedOption.label,
        };

        // Rebuild advanced settings when the field changes so stale values don't persist
        const calcType = updated.fields[index].calculationType;
        if (calcType) {
          const validTypes = getCalculationTypesForField(updated.fields[index].field);
          const isCurrentTypeAllowed = validTypes.some(
            (type) => String(type.value) === String(calcType.type)
          );

          if (!isCurrentTypeAllowed) {
            updated.fields[index] = {
              ...updated.fields[index],
              calculationType: null,
              percentageSettings: undefined,
              npsSettings: undefined,
              numericSettings: undefined,
            };
          } else {
            const calcTypeKey = String(calcType.type);
            const strategy = CALC_TYPE_DEFAULTS[calcTypeKey];
            if (strategy) {
              Object.assign(
                updated.fields[index],
                strategy.buildDefaults(updated.fields[index].field)
              );
            }
          }
        }
      }
      onSelectedUserFieldsChange?.(updated);
    },
    [
      selectedUserFields,
      fieldsManagerViewOptions,
      getCalculationTypesForField,
      onSelectedUserFieldsChange,
    ]
  );

  /** Generic helper to update a single field by index. */
  const updateFieldAt = useCallback(
    (
      index: number,
      patch: Partial<SelectedFieldModel> | ((f: SelectedFieldModel) => Partial<SelectedFieldModel>)
    ) => {
      const Fields = selectedUserFields.fields.map((f, i) => {
        if (i !== index) return f;
        const p = typeof patch === 'function' ? patch(f) : patch;
        return { ...f, ...p };
      });
      onSelectedUserFieldsChange?.({ ...selectedUserFields, fields: Fields });
    },
    [selectedUserFields, onSelectedUserFieldsChange]
  );

  const handleDisplayNameChange = useCallback(
    (index: number, value: string) => {
      updateFieldAt(index, { displayName: value });
    },
    [updateFieldAt]
  );

  const handleCalculationTypeChange = useCallback(
    (index: number, selectedOption: any) => {
      const updated = { ...selectedUserFields, fields: [...selectedUserFields.fields] };
      if (selectedOption) {
        const calcType = fieldsManagerViewOptions?.CalculationTypeOptions?.find(
          (type) => String(type.type) === String(selectedOption.value)
        );
        const availableTypes = getCalculationTypesForField(updated.fields[index].field);
        const isAllowed = availableTypes.some(
          (type) => String(type.value) === String(calcType?.type)
        );

        updated.fields[index].calculationType = calcType && isAllowed ? calcType : null;

        const strategy =
          calcType && isAllowed ? CALC_TYPE_DEFAULTS[String(calcType.type)] : undefined;
        const defaults = strategy?.buildDefaults(updated.fields[index].field) ?? {
          isL1Category: false,
        };
        updated.fields[index] = {
          ...updated.fields[index],
          percentageSettings: undefined,
          npsSettings: undefined,
          numericSettings: undefined,
          ...defaults,
        };

        if (
          strategy?.autoTouchWhenIncomplete &&
          isPercentageSettingsIncomplete(updated.fields[index])
        ) {
          setTouchedFieldIndices((prev) => new Set([...Array.from(prev), index]));
        }
      } else {
        updated.fields[index] = {
          ...updated.fields[index],
          calculationType: null,
          percentageSettings: undefined,
          npsSettings: undefined,
          numericSettings: undefined,
        };
      }
      onSelectedUserFieldsChange?.(updated);
    },
    [
      selectedUserFields,
      fieldsManagerViewOptions,
      getCalculationTypesForField,
      onSelectedUserFieldsChange,
    ]
  );

  const handlePrecisionChange = useCallback(
    (index: number, selectedOption: any) => {
      updateFieldAt(index, { precision: selectedOption.value.toString() });
    },
    [updateFieldAt]
  );

  const handleNormalizeChange = useCallback(
    (index: number, checked: boolean) => {
      updateFieldAt(index, { normalize: checked });
    },
    [updateFieldAt]
  );

  const handleStatTestingChange = useCallback(
    (index: number, checked: boolean) => {
      updateFieldAt(index, { statTesting: checked });
    },
    [updateFieldAt]
  );

  const handleIsL1CategoryChange = useCallback(
    (index: number, checked: boolean) => {
      updateFieldAt(index, { isL1Category: checked });
    },
    [updateFieldAt]
  );

  const handleReorderFields = useCallback(
    (draggedIndex: number, targetIndex: number) => {
      // The pinned Project Frequency field is fixed at the top: it cannot be
      // dragged, and no other field may take its position.
      if (
        selectedUserFields.fields[draggedIndex]?.isProjectFrequency ||
        selectedUserFields.fields[targetIndex]?.isProjectFrequency
      ) {
        return;
      }
      if (draggedIndex !== targetIndex) {
        const reordered = arrayMove(selectedUserFields.fields, draggedIndex, targetIndex);
        const fields = clearNewFieldDisplayName(reordered);
        const updated = { ...selectedUserFields, fields: fields };
        setTouchedFieldIndices(
          (prev) => new Set([...Array.from(prev), ...updated.fields.map((_, i) => i)])
        );
        onSelectedUserFieldsChange?.(updated);
      }
    },
    [selectedUserFields, onSelectedUserFieldsChange]
  );

  const handleAccordionToggle = useCallback((index: number) => {
    setExpandedFieldIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  /** Collapses every field card. */
  const collapseAllFields = useCallback(() => {
    setExpandedFieldIndices(new Set());
  }, []);

  /** Expands every field card. */
  const expandAllFields = useCallback(() => {
    setExpandedFieldIndices(new Set(selectedUserFields.fields.map((_, i) => i)));
  }, [selectedUserFields.fields]);

  /**
   * Handles blur on a field-item to trigger per-field validation.
   * Uses the element ref instead of document.getElementById.
   */
  const handleFieldItemBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>, index: number) => {
      // Ignore blur caused by react-select menu portals (rendered outside the field-item DOM)
      if (
        e.relatedTarget instanceof HTMLElement &&
        (e.relatedTarget.closest('[class*="-menu"]') ||
          e.relatedTarget.id?.includes('react-select'))
      ) {
        return;
      }

      const currentFieldItemId = `field-item-${index}`;
      const currentFieldItem = fieldItemRefs.current.get(index) ?? null;
      const relatedNode = e.relatedTarget instanceof Node ? e.relatedTarget : null;

      if (focusedFieldId == null) {
        setFocusedFieldId(currentFieldItemId);
        if (currentFieldItem?.contains(relatedNode)) return;
        setFocusedFieldId(currentFieldItemId);
        setTouchedFieldIndices((prev) => new Set([...Array.from(prev), index]));
        return;
      }

      if (focusedFieldId === currentFieldItemId && currentFieldItem?.contains(relatedNode)) {
        return;
      }

      setFocusedFieldId(currentFieldItemId);
      setTouchedFieldIndices((prev) => new Set([...Array.from(prev), index]));
    },
    [focusedFieldId]
  );

  // --- Filter handlers (persisted on SelectedFieldModel) ---

  /** Adds a new filter to the field at the given index. */
  const handleFilterAdd = useCallback(
    (index: number, newFilter: FieldFilter) => {
      updateFieldAt(index, (f) => {
        const current = f.filters ?? [];
        if (current.length >= 5) return {};
        return { filters: [...current, newFilter] };
      });
    },
    [updateFieldAt]
  );

  /** Removes a filter by its id from the field at the given index. */
  const handleFilterRemove = useCallback(
    (index: number, filterId: string) => {
      updateFieldAt(index, (f) => ({
        filters: (f.filters ?? []).filter((fil) => fil.id !== filterId),
      }));
    },
    [updateFieldAt]
  );

  /** Updates a single filter's properties via a JSON-encoded payload. */
  const handleFilterValueChange = useCallback(
    (index: number, filterId: string, valueGuid: string) => {
      const payload = JSON.parse(valueGuid);
      updateFieldAt(index, (f) => ({
        filters: (f.filters ?? []).map((fil) =>
          fil.id === filterId ? { ...fil, ...payload } : fil
        ),
      }));
    },
    [updateFieldAt]
  );

  // --- Derived values ---

  const dataSourceDisplayName = useMemo(() => {
    if (!fieldsManagerViewOptions?.parentPartition) return 'Unknown Data Source';
    return fieldsManagerViewOptions.parentPartition.label || 'Unknown Data Source';
  }, [fieldsManagerViewOptions?.parentPartition]);

  const fieldCountText = useMemo(
    () =>
      getDataSourceFieldCount(fieldsManagerViewOptions?.parentPartition ?? null, [
        selectedUserFields,
      ]),
    [fieldsManagerViewOptions?.parentPartition, selectedUserFields]
  );

  /** Text describing how many partition filters are currently applied. */
  const filterCountText = useMemo(() => {
    const totalFilters = selectedUserFields.partitionFilters?.length ?? 0;
    if (totalFilters === 0) return 'No filters';
    return totalFilters === 1 ? '1 filter' : `${totalFilters} filters`;
  }, [selectedUserFields]);

  /**
   * True when at least one field card is currently rendering its own invalid state.
   * Uses the very same criteria as FieldItemCard (touched-aware field validation OR
   * touched-aware filter validation), so the header warning and the card warnings
   * always appear and disappear together.
   */
  const hasInvalidFields = useMemo(
    () =>
      selectedUserFields.fields.some((field, index) => {
        const isTouched = touchedFieldIndices.has(index);
        return (
          isFieldItemInvalid(field, index, touchedFieldIndices) ||
          areFiltersInvalid(field.filters ?? [], isTouched)
        );
      }),
    [selectedUserFields, touchedFieldIndices]
  );

  /**
   * True when at least one partition-level filter is incomplete (missing field,
   * operator, or values). Unlike field filters, partition filters have no
   * per-row "touched" concept, so any incomplete row is flagged immediately.
   */
  const hasInvalidPartitionFilters = useMemo(
    () => hasIncompleteFilters(selectedUserFields.partitionFilters ?? []),
    [selectedUserFields]
  );

  return {
    // State
    isLoading,
    availableFields,
    availableFilterFields,
    calculationTypes,
    getCalculationTypesForField,
    expandedFieldIndices,
    highlightedFieldIndex,
    touchedFieldIndices,
    fieldStates,
    dataSourceDisplayName,
    fieldCountText,
    filterCountText,
    hasInvalidFields,
    hasInvalidPartitionFilters,

    // Ref setter
    setFieldItemRef,

    // Handlers
    handleAddField,
    handleRemoveField,
    handleFieldSelect,
    handleDisplayNameChange,
    handleCalculationTypeChange,
    handlePrecisionChange,
    handleNormalizeChange,
    handleStatTestingChange,
    handleIsL1CategoryChange,
    handleReorderFields,
    handleAccordionToggle,
    collapseAllFields,
    expandAllFields,
    handleFieldItemBlur,
    handleFilterAdd,
    handleFilterRemove,
    handleFilterValueChange,
  };
};
